import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { AnyAction } from 'redux';
import { Spin } from 'antd';
import { Permissions } from '@liveline/ll_node_common';
import { PermissionsObject } from '@liveline/ll_node_common/lib/Permissions/types';

import { authActions } from './state/features/authReducer';

import { AbilityContext } from './components/shared/Can';

import "./app.scss";
import ContentRoutes from "./layout/routes";
import { history } from "./utils/history";

import { ErrorBoundary } from "react-error-boundary"; // Error catch library-
import ErrorBoundaryFallback from "./layout/private-routes/error-boundary/index"; //This is the fallback component-

// custom hook-
import useSystemLanguage from "./hooks/useSystemLanguage";
import { RootState } from "./state/features/reducers";

import { FacilityUserRole } from './services/apiTypes';
import useApiClient from './services/useApiClient';
import { setToleranceSource, resetToleranceSource } from './components/monitor/signal-data/equipt-content/sensor-chart/toleranceCategoryUtils';

function App() {
    const errorHandler = (error: any, errorInfo: any) => {
        console.log("Logging: ", error, errorInfo);
    };

    history.location = useLocation();
    history.navigate = useNavigate();

    useSystemLanguage();

    const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
    const rehydrating = useSelector((x: RootState) => x?.auth?.rehydrating);

    // Tokens are never persisted (LT-2998) — resolve the HttpOnly session
    // cookie into in-memory tokens once per page load
    useEffect(() => {
        dispatch(authActions.rehydrateSession());
    }, [dispatch]);

    // LT-3221: hydrate the shared chart tolerance source once per facility so console charts
    // draw the same spec bands db_api serves (and mobile uses), instead of a hardcoded copy.
    // Reset to the built-in seed on every facility change first, so a failed/absent fetch shows
    // the seed rather than the PREVIOUS facility's bands.
    const client = useApiClient();
    const selectedFacilityId = useSelector((x: RootState) => (x as any)?.userSelection?.facility?.id);
    useEffect(() => {
        resetToleranceSource();
        if (!client || !selectedFacilityId) return;
        let cancelled = false;
        client.fetchChartToleranceSettings(selectedFacilityId).then((entity: any) => {
            if (!cancelled && entity?.data) setToleranceSource(entity.data);
        });
        return () => {
            cancelled = true;
        };
    }, [client, selectedFacilityId]);

    const ability = useSelector((x:RootState) => {
        const permissionsArray: FacilityUserRole[] = x?.auth?.user?.permissions ?? [];
        
        // Cleanup the permissions object
        // (We ensure that the item is not null/undefined and the 'id' property exists)
        const validPermissions = permissionsArray.filter(
            (p): p is FacilityUserRole => !!p && typeof p.id === 'string'
        );
        const finalPermissions = validPermissions as PermissionsObject[]; 

        return Permissions.defineAbility(finalPermissions);
    });

    // Hold rendering until rehydration settles: routes mounted now would fire
    // API calls without a token and bounce to the login screen
    if (rehydrating) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <ErrorBoundary
            FallbackComponent={ErrorBoundaryFallback}
            onError={errorHandler}
        >
            <AbilityContext.Provider value={ability}>
                <ContentRoutes />
            </AbilityContext.Provider>
        </ErrorBoundary>
    );
}

export default App;
